import { useEffect, useState } from "react";
import { Link, useBlocker, useNavigate } from "react-router-dom";
import { ChevronLeft, BookOpen, Save } from "lucide-react";
import styles from "@/components/sheet/LembarVisual.module.css";
import { Button } from "@/components/ax/Button";
import { Field } from "@/components/ax/Field";
import { PrimaryBar } from "@/components/ax/PrimaryBar";
import { Sheet } from "@/components/ax/Sheet";
import { KindChips } from "@/components/sheet/KindChips";
import { PromoteSheet } from "@/components/sheet/PromoteSheet";
import { Reading, ReadingSkeleton } from "@/components/sheet/Reading";
import { SheetMenu } from "@/components/sheet/SheetMenu";
import { useSheet } from "@/hooks/useSheet";
import { useI18n } from "@/i18n";

function primaryFor(s, t) {
  if (s.busy === "read") return { label: t("sheet.reading"), disabled: true, busy: true };
  if (s.isNew) return { label: t("sheet.save"), disabled: !s.textValid || !!s.busy, run: s.save };
  if (s.addOpen) return { label: t("sheet.saveAdd"), disabled: s.addText.trim().length < 20 || !!s.busy, run: s.saveAddition };
  if (s.dirty) return { label: t("sheet.save"), disabled: !!s.busy, run: s.save };
  if (!s.aiOn || s.aiPaused) return null;
  if (s.left <= 0) return s.hasQuestions ? { label: t("sheet.promote"), run: () => s.openPromote(null) } : null;
  if (s.last?.role === "user") return { label: t("sheet.read"), disabled: !!s.busy, run: s.requestReading };
  if (s.hasQuestions) return { label: t("sheet.promote"), run: () => s.openPromote(null) };
  if (s.blocksUser.length >= 3) return { label: t("sheet.add"), disabled: true };
  return { label: t("sheet.add"), run: () => s.setAddOpen(true) };
}

export default function LembarEditor() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const s = useSheet();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const primary = primaryFor(s, t);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => s.guard.current && s.dirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!s.dirty) return undefined;
    const warn = (e) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [s.dirty]);

  if (s.state === "loading") {
    return (
      <div aria-busy="true" data-testid="sheet-editor-skeleton" className={`${styles.editorSkeleton} flex flex-col gap-4`}>
        <div className="h-8 w-1/2 rounded-[8px] bg-ax-border" />
        <div className="h-12 w-full rounded-[8px] bg-ax-border" />
        <div className="h-[180px] w-full rounded-[8px] bg-ax-border" />
      </div>
    );
  }

  if (s.state === "error") {
    return (
      <div role="alert" data-testid="sheet-editor-error" className={`enter ${styles.error}`}>
        <Link to="/lembar" className={styles.backLink}>{t("sheet.title")}</Link>
        <p className="m-0 mt-4 text-base text-ax-text2">{t("sheet.failLoad")}</p>
        <Button onClick={s.retry} data-testid="sheet-editor-retry-button" className="mt-4">
          {t("retry")}
        </Button>
      </div>
    );
  }

  const canAdd = s.left > 0 && s.blocksUser.length < 3;
  const fmt = new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "id-ID", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div data-testid="sheet-editor-page" className={styles.editor}>
      <div className={styles.editorToolbar}>
        <Link
          to="/lembar"
          data-testid="sheet-back-link"
          className={styles.backLink}
        >
          <ChevronLeft size={20} strokeWidth={1.5} aria-hidden="true" />
          {t("sheet.title")}
        </Link>
        <div className={styles.editorStatus}>
          <span className={styles.count} data-testid="sheet-editor-status">{t(s.isNew || s.dirty ? "sheet.draft" : `sheet.status.${s.sheet?.status ?? "open"}`)}</span>
          {!s.isNew && <SheetMenu onStatus={s.setStatus} onDelete={() => setConfirmDelete(true)} />}
        </div>
      </div>

      {s.flash && (
        <p role="status" data-testid="sheet-flash" className="m-0 mt-1 text-sm leading-5 text-ax-link">
          {s.flash}
        </p>
      )}

      <KindChips
        legend={t("sheet.classification")}
        value={s.form.kind}
        onChange={(kind) => s.setForm({ ...s.form, kind })}
        testId="sheet-kind"
        className={styles.classification}
      />

      <Field
        label={t("sheet.date")}
        type="date"
        value={s.form.entryDate}
        onChange={(e) => s.setForm({ ...s.form, entryDate: e.target.value })}
        error={s.errors.entryDate}
        testId="sheet-date-input"
        className={styles.dateField}
      />

      <Field
        label={t("sheet.titleLabel")}
        placeholder={t("sheet.titlePh")}
        value={s.form.title}
        maxLength={80}
        onChange={(e) => s.setForm({ ...s.form, title: e.target.value })}
        error={s.errors.title}
        testId="sheet-title-input"
        className={styles.titleField}
      />
      {s.form.title.length > 64 && (
        <p data-testid="sheet-title-count" className="m-0 mt-2 font-mono text-[13px] leading-[18px] text-ax-muted">
          {s.form.title.length}/80
        </p>
      )}

      {s.firstClosed ? (
        <section data-testid="sheet-block-1" className={styles.noteField}>
          <div className={styles.noteLabel}><span>{t("sheet.body")}</span><span className={styles.characterCount}>{t("sheet.characters", { count: s.blocksUser[0].text.length })}</span></div>
          <p className={styles.savedNote}>
            {s.blocksUser[0].text}
          </p>
        </section>
      ) : (
        <Field
          label={<span className={styles.noteLabel}><span>{t("sheet.body")}</span><span className={styles.characterCount}>{t("sheet.characters", { count: s.form.text.length })}</span></span>}
          multiline
          value={s.form.text}
          maxLength={6000}
          style={{ minHeight: 260 }}
          onChange={(e) => s.setForm({ ...s.form, text: e.target.value })}
          hint={t("sheet.hint")}
          placeholder={t("sheet.hint")}
          error={s.errors.text}
          testId="sheet-body-input"
          className={styles.noteField}
        />
      )}

      {s.sheet?.blocks.slice(1).map((b) =>
        b.role === "reading" ? (
          <Reading
            key={b.id}
            block={b}
            index={s.readings.findIndex((r) => r.id === b.id) + 1}
            canRaise={b.id === s.lastReading?.id}
            onRaise={(q) => s.openPromote(q)}
          />
        ) : (
          <section key={b.id} data-testid={`sheet-block-${b.id}`} className={styles.noteField}>
            <p className="m-0 text-sm font-medium leading-5 text-ax-text">
              {t("sheet.addLabel")} · {fmt.format(new Date(b.at))}
            </p>
            <p className={styles.savedNote}>
              {b.text}
            </p>
          </section>
        ),
      )}

      {s.busy === "read" && <ReadingSkeleton />}

      {s.addOpen && (
        <Field
          label={t("sheet.addLabel")}
          multiline
          autoFocus
          value={s.addText}
          maxLength={2000}
          onChange={(e) => s.setAddText(e.target.value)}
          error={s.errors.addText}
          testId="sheet-add-input"
          className={styles.noteField}
        />
      )}

      {!s.isNew && !s.aiOn && (
        <p data-testid="sheet-ai-off" className={styles.editorNotice}>
          {t("sheet.aiOff")}{" "}
          <Link to="/akun" data-testid="sheet-consent-link" className="text-ax-link hover:underline">
            {t("acc.privacy")}
          </Link>
        </p>
      )}

      {!s.isNew && s.aiOn && s.aiPaused && (
        <p data-testid="sheet-ai-paused" className={styles.editorNotice}>
          {t("sheet.errDisabled")}
        </p>
      )}

      {!s.isNew && s.aiOn && !s.aiPaused && s.left <= 0 && (
        <p data-testid="sheet-quota-out" className={styles.editorNotice}>
          {t("sheet.quota")}
        </p>
      )}

      {!s.isNew && s.blocksUser.length >= 3 && (
        <p data-testid="sheet-limit" className={styles.editorNotice}>
          {t("sheet.limit")}
        </p>
      )}

      {s.errors.form && (
        <p role="alert" data-testid="sheet-form-error" className="m-0 mt-4 text-sm leading-5 text-ax-error">
          {s.errors.form}
        </p>
      )}

      <PrimaryBar>
        <div className={`${styles.editorActions} flex flex-wrap items-center gap-x-4 gap-y-2`}>
          {primary && (
            <Button
              variant="primary"
              busy={primary.busy}
              disabled={primary.disabled}
              onClick={primary.run}
              data-testid="sheet-primary-button"
              className="w-full md:w-auto md:min-w-[200px]"
            >
              {primary.run === s.requestReading ? <BookOpen size={18} strokeWidth={1.5} aria-hidden="true" /> : (s.isNew || s.dirty || s.addOpen) ? <Save size={18} strokeWidth={1.5} aria-hidden="true" /> : null}
              {primary.label}
            </Button>
          )}
          {!s.dirty && !s.addOpen && s.hasQuestions && canAdd && (
            <Button variant="ghost" onClick={() => s.setAddOpen(true)} data-testid="sheet-add-button" className="px-0">
              {t("sheet.add")}
            </Button>
          )}
          {!s.isNew && s.left <= 0 && !s.hasQuestions && (
            <Button variant="ghost" onClick={() => navigate("/lembar/baru")} data-testid="sheet-new-link" className="px-0">
              {t("sheet.new")}
            </Button>
          )}
        </div>
      </PrimaryBar>

      <PromoteSheet
        s={s}
        onDone={(decisionId) => setTimeout(() => navigate("/keputusan/baru", { state: { decisionId } }), 900)}
      />

      <Sheet
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t("sheet.delete")}
        testId="sheet-delete-sheet"
      >
        <Button variant="danger" onClick={s.remove} data-testid="sheet-delete-confirm" className="w-full">
          {t("sheet.deleteCta")}
        </Button>
        <Button onClick={() => setConfirmDelete(false)} data-testid="sheet-delete-keep" className="mt-2 w-full">
          {t("sheet.keep")}
        </Button>
      </Sheet>

      <Sheet
        open={blocker.state === "blocked"}
        onOpenChange={(o) => !o && blocker.reset?.()}
        title={t("dirty.title")}
        testId="sheet-dirty-sheet"
      >
        <Button variant="primary" onClick={() => blocker.reset()} data-testid="sheet-dirty-stay" className="w-full">
          {t("dirty.stay")}
        </Button>
        <Button onClick={() => blocker.proceed()} data-testid="sheet-dirty-discard" className="mt-2 w-full">
          {t("dirty.discard")}
        </Button>
      </Sheet>
    </div>
  );
}
